// @flow
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Collapsible from '../../../../elements/Collapsible/Collapsible';
import {Grid, Box, InputAdornment, IconButton} from '@material-ui/core';
import {Delete} from '@material-ui/icons';
import {colors} from '../../../../assets/styles/colors';
import {useIsFeatureInTier} from '../../../../utils/component';
import TierRestrictionModal from '../../../../elements/TierRestrictionModal/TierRestrictionModal';
import {useHistory} from 'react-router-dom';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {useForm} from 'react-hook-form';

import {useTikTokIntegration} from '../utils';
import InlineLoader from '../../../../elements/LoadingSpinner/InlineLoader';

const useStyles = makeStyles(theme => ({
  subTitle: {
    marginBottom: theme.spacing(2),
    fontSize: '0.875rem',
    color: colors.grey700,
  },
  marginTopNone: {
    marginTop: '0 !important',
  },
  logoImg: {
    width: 20,
    height: 20,
    opacity: 0.6,
    marginTop: '14px',
    marginRight: theme.spacing(2),
  },
  facebookButton: {
    marginTop: theme.spacing(3),
    width: '100%',
    height: 56,
    background: 'linear-gradient(180deg, #1778F2 0%, #0A6CE7 100%)',
    borderRadius: '12px',
    color: 'white',
    justifyContent: 'flex-start',
  },
  copyFbLinkBtn: {
    textTransform: 'none',
    borderRadius: '8px',
    maxWidth: '100%',
    width: 400,
    whiteSpace: 'nowrap',
  },
  copyFbLinkBtnLabel: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  divider: {
    margin: theme.spacing(5, 0),
    background: colors.grey200,
    width: '100%',
  },
  sectionTitle: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitleFont: {
    fontWeight: 600,
  },
  connectionState: {
    marginRight: theme.spacing(1),
    fontSize: '0.875rem',
    color: colors.grey700,
  },
  connected: {
    color: colors.success,
  },

  description: {
    color: '#9595AB',
    fontSize: 14,

    '& a': {
      color: colors.primary,
    },
  },
  form: {
    position: 'relative',
  },
  loading: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(4px)',
    zIndex: 10,
  },
  snippetTextField: {
    '& textarea': {
      fontFamily: 'monospace',
      fontSize: 12,
    },
  },
  extractedIdBox: {
    padding: '.5rem',
    backgroundColor: colors.grey100,
    borderRadius: '.75rem',
    border: `1px solid ${colors.grey300}`,
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    justifyContent: 'space-between',
  },
  extractedIdText: {
    fontSize: 14,
    color: colors.grey900,
    fontWeight: 500,
  },
}));

const FORM_FIELDS = {
  code: 'code',
};

const extractTikTokPixelId = (snippet: string): string | null => {
  if (!snippet) return null;

  // Pattern: ttq.load('XXXXXXXXXX') - handles both single and double quotes
  const match = snippet.match(/ttq\.load\s*\(\s*['"]([A-Z0-9]+)['"]\s*\)/i);
  if (match) return match[1];

  // Direct ID pattern (alphanumeric, typically 15-20 characters)
  const directMatch = snippet.match(/\b([A-Z0-9]{15,20})\b/);
  if (directMatch) return directMatch[1];

  return null;
};

export default function TikTokPixelIntegration() {
  const classes = useStyles();
  const history = useHistory();

  const {integration, loading, saveTikTokIntegration} = useTikTokIntegration();

  const {handleSubmit, register, setValue, errors, watch} = useForm({
    mode: 'onChange',
  });

  const [tierRestrictionModal, setTierRestrictionModal] = useState(false);
  const [codeSnippet, setCodeSnippet] = useState('');
  const [extractedId, setExtractedId] = useState('');

  const hasTiktokPixelFeature = useIsFeatureInTier('TIKTOK_PIXEL_ID');

  useEffect(() => {
    if (!hasTiktokPixelFeature) {
      setTierRestrictionModal(true);
    }
  }, [hasTiktokPixelFeature]);

  useEffect(() => {
    if (!integration) return;

    const {pixelId} = integration;
    if (pixelId) {
      setValue(FORM_FIELDS.code, pixelId);
    }
  }, [integration, setValue]);

  const handleSnippetChange = event => {
    const snippet = event.target.value;
    setCodeSnippet(snippet);

    // Extract pixel ID from snippet
    const id = extractTikTokPixelId(snippet);
    if (id) {
      setExtractedId(id);
      if (!tiktokPixelCode) {
        setValue(FORM_FIELDS.code, id);
      }
    } else {
      setExtractedId('');
    }
  };

  const handleReplacePixelId = () => {
    setValue(FORM_FIELDS.code, extractedId);
    const request = {
      pixelId: extractedId,
    };

    saveTikTokIntegration(request);

    setExtractedId('');
    setCodeSnippet('');
  };

  const handleRemovePixelId = () => {
    setValue(FORM_FIELDS.code, '');
    setExtractedId('');
    setCodeSnippet('');
  };

  const onSubmit = (data: any) => {
    const {code} = data;

    const request = {
      pixelId: code,
    };

    saveTikTokIntegration(request);

    setExtractedId('');
    setCodeSnippet('');
  };

  const tiktokPixelCode = watch(FORM_FIELDS.code, '');

  return (
    <>
      <Collapsible
        title={
          <div className={classes.sectionTitle}>
            <Typography className={classes.sectionTitleFont}>
              Conexión con TikTok pixel
            </Typography>
            {/*             <Typography
              className={`${classes.connectionState} ${
                !!tiktokPixelCode ? classes.connected : ''
              }`}
            >
              Estado de conexión:{' '}
              {tiktokPixelCode ? 'Conectado' : 'Desconectado'}
            </Typography> */}
          </div>
        }
      >
        <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
          {loading && (
            <div className={classes.loading}>
              <InlineLoader size={24} loading />
            </div>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className={classes.description}>
                Obtén información detallada para optimizar tu tienda. Copia y
                pega tu ID de TikTok Pixel o el código completo y supervisa las
                actividades de tu tienda directamente desde el panel de{' '}
                <a
                  href="https://ads.tiktok.com/help/article/about-tiktok-events-manager"
                  target="_BLANK"
                  rel="noopener noreferrer"
                >
                  TikTok Events Manager.
                </a>
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <CustomTextField
                placeholder="Pixel ID (ej: XXXXXXXXXXXXXXX)"
                value={tiktokPixelCode}
                name={FORM_FIELDS.code}
                error={!!errors[FORM_FIELDS.code]}
                helperText={
                  errors[FORM_FIELDS.code]
                    ? errors[FORM_FIELDS.code].message
                    : ''
                }
                inputRef={register({
                  required: 'Campo obligatorio',
                })}
                type="text"
                disabled={loading}
                InputProps={{
                  endAdornment: integration?.pixelId && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleRemovePixelId}>
                        <Delete color="error" size="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Typography
              variant="body2"
              style={{textAlign: 'center', marginInline: 'auto'}}
              className={classes.description}
            >
              o copia y pega el código completo de TikTok Pixel
            </Typography>

            <Grid item xs={12}>
              <Box>
                <CustomTextField
                  placeholder="Pega aquí el código completo de TikTok Pixel..."
                  className={classes.snippetTextField}
                  value={codeSnippet}
                  onChange={handleSnippetChange}
                  multiline
                  rows={8}
                  disabled={loading}
                />
                {extractedId && (
                  <Box mt={2} className={classes.extractedIdBox}>
                    <Typography className={classes.extractedIdText}>
                      ID detectado: {extractedId}
                    </Typography>
                    {integration?.pixelId && (
                      <CustomButton
                        variant="outlined"
                        onClick={handleReplacePixelId}
                      >
                        Reemplazar ID
                      </CustomButton>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <CustomButton
                className={classes.saveButton}
                variant="contained"
                type="submit"
              >
                Guardar Cambios
              </CustomButton>
            </Grid>
          </Grid>
        </form>
      </Collapsible>

      <TierRestrictionModal
        open={tierRestrictionModal}
        handleClose={() => history.replace('/my-store/social-medias')}
      />
    </>
  );
}
